import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { observable, computed } from 'mobx-angular';
import { Account } from 'netsocial-lib/src/app/interfaces/account';
import { ConnectionAuthUrl } from '../../../interfaces/connection-auth-url';
import { ConnectionService } from '../../../services/connection/connection.service';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import { AccountService } from 'netsocial-posting/src/app/services/account/account.service';
import { ApiRequestService } from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { Router } from '@angular/router';
import { getAppUrl } from 'netsocial-lib/src/app/utils/app-utils';
import { from, Subscription } from 'rxjs';
import { propertyDidChange } from 'netsocial-lib/src/app/utils/component-utils';
import { InstagramAuthComponent } from '../instagram-auth/instagram-auth.component';

@Component({
  selector: 'account-connect',
  templateUrl: './account-connect.component.html',
  styleUrls: ['./account-connect.component.scss'],
})
export class AccountConnectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() loading: boolean;
  @Input() showAccounts: boolean;

  @Input() showTitles: boolean;
  @Input() slim: boolean;

  @observable @Input() accounts: Account[];
  @observable @Input() connections?: ConnectionAuthUrl[];

  ionRefresh: () => void;

  @Output() ionAccountsLoaded: any = new EventEmitter<Account[]>();

  @computed
  get accountsByService(): Record<string, Account[]> {
    return (this.accounts ? this.accounts : []).reduce(
      (hash: { [service: string]: Account[] }, account) => {
        hash[account.service] = hash[account.service] ? hash[account.service] : [];
        hash[account.service].push(account);
        return hash;
      },
      {}
    );
  }

  public modal: HTMLIonModalElement;

  contextOwnerSubscription: Subscription;
  @observable getConnectionsSubscription?: Subscription;

  @computed
  get isLoading(): boolean {
    return this.loading || !!this.getConnectionsSubscription;
  }

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly accountService: AccountService,
    private readonly errorService: ErrorService,
    private readonly apiRequest: ApiRequestService,
    private readonly router: Router,
    private readonly modalController: ModalController
  ) {}

  ngOnInit(): void {
    this.apiRequest.getContextOwner().subscribe(() => {
      this.getConnectionsSubscription = from(this.getConnections()).subscribe(() => {
        this.getConnectionsSubscription = undefined;
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (propertyDidChange(changes).length) {
      this.getConnections();
    }
  }

  ngOnDestroy(): void {
    this.getConnectionsSubscription?.unsubscribe();
  }

  async deleteAccount(account: Account): Promise<void> {
    this.errorService.resolve(this.accountService.deleteAccount(account.id), {
      successCallback: () => {
        this.getConnections();
      },
    });
  }
  finishTourStep(): void {
    this.modal.dismiss();
    this.router.navigate(['workspace/social-accounts'], {
      queryParams: { tourResources: true },
    });
  }

  async instagramButton(connection: ConnectionAuthUrl): Promise<void> {
    const modal = await this.modalController.create({
      component: InstagramAuthComponent,
      cssClass: 'custom-class',
      componentProps: {
        connection: connection,
      },
    });

    return await modal.present();
  }

  async getConnections(): Promise<void> {
    let callback = `${getAppUrl()}${location.pathname}${location.search}`;
    callback += `${location.search ? '&' : '?'}showResources=true`;
    this.connections = await this.errorService.resolve(
      this.connectionService.getConnectionAuthUrls({
        callback,
      }),
      {
        errorMessage: 'components.accountConnect.errors.getConnections',
      }
    );

    if (this.showAccounts) {
      await this.errorService.resolve(this.accountService.getAccounts(), {
        errorMessage: 'components.accountConnect.errors.getAccounts',
        successCallback: (paginatedAccounts) => {
          this.accounts = [...paginatedAccounts.items];
          if ('emit' in this.ionAccountsLoaded) {
            this.ionAccountsLoaded.emit(this.accounts);
          } else {
            this.ionAccountsLoaded(this.accounts);
          }
        },
      });
    }
  }
}
